import {
  getChildren,
  getItemValue,
  getOptions,
} from "@shared/features/configuration/utils";
import { getDataByOrderType } from "@shared/features/tradingTerminal/common/utils";
import { useContext, useEffect, useMemo, useState } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { LanguageNameSpaces, OrderTypes } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { ORDER_TYPE_TO_TRAILING_TITLE } from "@shared/features/tradingTerminal/common/constants";

const useTrailingEntryAndTakeProfit = (orderType: OrderTypes) => {
  const {
    getCreateGeneralDataQuery,
    trailingEntry,
    setTrailingEntry,
    trailingTakeProfit,
    setTrailingTakeProfit,
    setUseTrailing,
    setValuesModified,
  } = useContext(TradingTerminalContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);

  const data = getCreateGeneralDataQuery?.currentData;

  const value =
    orderType === OrderTypes.ENTRY ? trailingEntry : trailingTakeProfit;

  const setTrailing =
    orderType === OrderTypes.ENTRY ? setTrailingEntry : setTrailingTakeProfit;

  const [selectorChanged, setSelectorChanged] = useState(false);

  useEffect(() => {
    if (data) {
      setSelectorChanged(false);
    }
  }, [data]);

  const trailingItem = useMemo(
    () => getChildren(data?.data.configuration)?.[0],
    [data]
  );

  const trailingItemByOrderType = useMemo(
    () => getDataByOrderType(getChildren(trailingItem), orderType),
    [trailingItem, orderType]
  );

  const initialValue = getItemValue(trailingItemByOrderType);

  const options = useMemo(
    () =>
      getOptions(trailingItemByOrderType)?.map(
        (option: { value: number; name: number }) => ({
          ...option,
          name: isNaN(option.name) ? option.name : `${option.name}%`,
        })
      ),
    [trailingItemByOrderType]
  );

  useEffect(() => {
    if (initialValue > 0 && !selectorChanged) {
      setTrailing(initialValue);
      setUseTrailing(orderType, true);
    } else if (!value && options?.length) {
      setTrailing(options[0].value);
    }
  }, [initialValue, value, options]);

  const onChange = (value: number) => {
    setSelectorChanged(true);
    setValuesModified(true);
    setTrailing(value);
  };

  return {
    options,
    value,
    onChange,
    title: t(ORDER_TYPE_TO_TRAILING_TITLE[orderType]),
  };
};

export default useTrailingEntryAndTakeProfit;
